// Stock counts and transfers on the web (M7d). Every change goes through the idempotent workflow RPCs; the
// tables themselves are read-only for users. Count schedule, owners and per-site status: CR_20260914.
import type { CountStatus } from '@fleetspoke/core/counts';
import type { LineStatus, Transfer, TransferPlace, TransferStatus } from '@fleetspoke/core/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export type Bucket = 'found' | 'not_found' | 'unexpected' | 'unverified';

export interface CountRow {
  id: string;
  site_id: string;
  status: 'open' | 'closed';
  window_start: string;
  window_end: string;
  created_at: string;
  created_by: string | null;
  closed_at: string | null;
  closed_by: string | null;
  scheduled_for: string | null;
  note: string | null;
  summary: {
    expected?: number;
    found?: number;
    not_found?: number;
    unverified?: number;
    unexpected?: number;
  };
  sites: { name: string } | null;
}

export interface CountLine {
  id: string;
  asset_id: string;
  bucket: Bucket;
  cleared_by: string | null;
  cleared_method: string | null;
  cleared_at: string | null;
  /**
   * How the count itself found it in the window (20260918130000_count_line_evidence); null = it did not.
   * Optional because a database without that migration simply has no such column.
   */
  found_method?: string | null;
  found_at?: string | null;
  assets: { asset_code: string; description: string | null } | null;
}

async function data<T>(
  q: PromiseLike<{
    data: unknown;
    error: { message: string; code?: string; details?: string | null; hint?: string | null } | null;
  }>,
): Promise<T> {
  const { data: d, error } = await q;
  // details + hint carry the dispatch refusal's blockers (CR_20260921 L6 — @fleetspoke/core/lifecycle)
  if (error)
    throw Object.assign(new Error(error.message), {
      code: error.code,
      details: error.details,
      hint: error.hint,
    });
  return d as T;
}

const COUNT_COLUMNS =
  'id, site_id, status, window_start, window_end, created_at, created_by, closed_at, closed_by, scheduled_for, note, summary, sites(name)';

export const workflowKeys = {
  counts: ['counts'] as const,
  count: (id: string) => ['counts', id] as const,
  /** under 'counts', so every count command refreshes it */
  status: (siteId?: string) => ['counts', 'status', siteId ?? ''] as const,
  owners: (siteId: string) => ['count-owners', siteId] as const,
  transfers: ['web-transfers'] as const,
  /** under 'web-transfers', so every transfer command and live refresh reaches it */
  placeMovements: (siteIds: string[], before: string) =>
    ['web-transfers', 'place', siteIds, before] as const,
};

/** Not yet received, disputed or cancelled. */
export const OPEN_TRANSFER_STATUSES: TransferStatus[] = ['draft', 'dispatched', 'awaiting_receipt'];
export const TRANSFER_STATUS_COLOR = {
  draft: 'default',
  dispatched: 'info',
  awaiting_receipt: 'warning',
  received: 'success',
  disputed: 'error',
  cancelled: 'default',
} as const;

/** count_status(): per visible active site — schedule, owners, state, last and open count (one site or all). */
export function useCountStatus(siteId?: string, enabled = true) {
  return useQuery({
    queryKey: workflowKeys.status(siteId),
    enabled,
    queryFn: () =>
      data<CountStatus[]>(supabase().rpc('count_status', siteId ? { p_site: siteId } : {})),
  });
}

/** The user ids named as a site's count owners (managers edit them in the site dialog). */
export function useCountOwnerIds(siteId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.owners(siteId ?? ''),
    enabled: !!siteId,
    queryFn: async () =>
      (
        await data<{ user_id: string }[]>(
          supabase().from('site_count_owners').select('user_id').eq('site_id', siteId!),
        )
      ).map((r) => r.user_id),
  });
}

/** Writes only the difference between the stored owners and `userIds`. */
export async function saveCountOwners(
  companyId: string,
  siteId: string,
  before: string[],
  userIds: string[],
): Promise<void> {
  const add = userIds.filter((u) => !before.includes(u));
  const drop = before.filter((u) => !userIds.includes(u));
  if (drop.length)
    await data(
      supabase().from('site_count_owners').delete().eq('site_id', siteId).in('user_id', drop),
    );
  if (add.length)
    await data(
      supabase()
        .from('site_count_owners')
        .insert(add.map((user_id) => ({ company_id: companyId, site_id: siteId, user_id }))),
    );
}

/** The last 100 counts at a place's sites (a warehouse, or a project's locations), newest first. */
export function useCounts(siteIds: readonly string[]) {
  const ids = [...siteIds].sort();
  return useQuery({
    queryKey: [...workflowKeys.counts, 'place', ids.join(',')],
    enabled: ids.length > 0,
    queryFn: () =>
      data<CountRow[]>(
        supabase()
          .from('stock_counts')
          .select(COUNT_COLUMNS)
          .in('site_id', ids)
          .order('created_at', { ascending: false })
          .limit(100),
      ),
  });
}

export function useCount(id: string, enabled = true) {
  return useQuery({
    queryKey: workflowKeys.count(id),
    enabled: enabled && !!id,
    queryFn: async () => {
      const [count, lines] = await Promise.all([
        data<CountRow>(supabase().from('stock_counts').select(COUNT_COLUMNS).eq('id', id).single()),
        data<CountLine[]>(
          // `*` on purpose, not a column list: naming found_method / found_at makes the whole request fail with
          // 42703 against a database that has not had 20260918130000_count_line_evidence yet, which breaks the page
          // outright rather than degrading. `*` returns whichever columns exist, and verifiedBy() treats the
          // missing ones as "no evidence recorded".
          supabase()
            .from('stock_count_lines')
            .select('*, assets(asset_code, description)')
            .eq('count_id', id),
        ),
      ]);
      return {
        count,
        lines: lines.sort((a, b) =>
          (a.assets?.asset_code ?? '').localeCompare(b.assets?.asset_code ?? ''),
        ),
      };
    },
  });
}

const rpc = <T>(fn: string, args: Record<string, unknown>) =>
  data<T>(supabase().rpc(fn, { p_idempotency_key: crypto.randomUUID(), ...args }));

/** A workflow command that refreshes counts, transfers and the site boards when it succeeds. */
function useCommand<A, R = unknown>(fn: (args: A) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of [
        ['counts'],
        workflowKeys.transfers,
        ['sites'],
        ['board'],
        ['assets'],
        ['billing'],
      ]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

export const useOpenCount = () =>
  useCommand((a: { siteId: string; hours: number }) =>
    rpc<{ count_id: string }>('open_stock_count', { p_site_id: a.siteId, p_window_hours: a.hours }),
  );
/** Always 'visual': from a desk nobody can scan a label or hear a tag, so the web tick is a person's word and the
 *  count line says so. Scans and BLE hears come from the phone. */
export const useClearLine = () =>
  useCommand((a: { countId: string; assetId: string }) =>
    rpc('clear_count_line', { p_count_id: a.countId, p_asset_id: a.assetId, p_method: 'visual' }),
  );
export const useCloseCount = () =>
  useCommand((a: { countId: string; note: string }) =>
    rpc('close_stock_count', { p_count_id: a.countId, p_note: a.note || null }),
  );
export const useCreateTransfer = () =>
  useCommand(
    (a: {
      fromSiteId: string | null;
      toSiteId: string;
      assetIds: string[];
      note: string;
      /** a manager's reason for dispatching past a certificate block (CR_20260921 L6a) */
      overrideReason?: string | null;
    }) =>
      rpc<{ transfer_id: string; ref: string; override?: boolean }>('create_transfer', {
        p_from_site_id: a.fromSiteId,
        p_to_site_id: a.toSiteId,
        p_asset_ids: a.assetIds,
        p_dispatch: true,
        p_note: a.note || null,
        ...(a.overrideReason ? { p_override_reason: a.overrideReason } : {}),
      }),
  );
export const useCancelTransfer = () =>
  useCommand((a: { transferId: string; note: string }) =>
    rpc('cancel_transfer', { p_transfer_id: a.transferId, p_note: a.note || null }),
  );
export const useReceiveTransfer = () =>
  useCommand((a: { transferId: string; assetIds: string[] | null }) =>
    rpc('receive_transfer', {
      p_transfer_id: a.transferId,
      p_asset_ids: a.assetIds,
      p_method: 'tap',
    }),
  );

export function useAllTransfers(enabled = true) {
  return useQuery({
    queryKey: workflowKeys.transfers,
    enabled,
    queryFn: () =>
      data<
        (Transfer & {
          note: string | null;
          received_at: string | null;
          /** the request it was made from (CR_20260915_Request_Qty); null when the viewer can't see it */
          request_id: string | null;
          asset_requests: { ref: string } | null;
        })[]
      >(
        supabase()
          .from('transfers')
          .select(
            'id, ref, status, from_site_id, to_site_id, created_by_system, vehicle_ref, dispatched_at, expected_at, awaiting_since, created_at, note, received_at, request_id, cert_override, asset_requests(ref), transfer_lines(id, asset_id, status, assets(asset_code, description))',
          )
          .order('created_at', { ascending: false })
          .limit(300),
      ),
  });
}

/** A transfer into, out of or between a project's sites, as the project page lists it. */
export interface ProjectMovement {
  id: string;
  ref: string;
  status: TransferStatus;
  from_site_id: string | null;
  to_site_id: string | null;
  created_by_system: boolean;
  dispatched_at: string | null;
  awaiting_since: string | null;
  received_at: string | null;
  received_by: string | null;
  receipt_method: 'tap' | 'gateway_auto' | 'scan' | null;
  created_at: string;
  updated_at: string;
  transfer_lines: {
    asset_id: string;
    status: LineStatus;
    assets: { asset_code: string } | null;
  }[];
}

/** Transfers touching any of the sites, created before `before` (ISO); the page narrows them to its period. */
export function usePlaceMovements(siteIds: string[], before: string) {
  const ids = [...siteIds].sort();
  return useQuery({
    queryKey: workflowKeys.placeMovements(ids, before),
    queryFn: () =>
      data<ProjectMovement[]>(
        supabase()
          .from('transfers')
          .select(
            'id, ref, status, from_site_id, to_site_id, created_by_system, dispatched_at, awaiting_since, received_at, received_by, receipt_method, created_at, updated_at, transfer_lines(asset_id, status, assets(asset_code))',
          )
          .or(`from_site_id.in.(${ids.join(',')}),to_site_id.in.(${ids.join(',')})`)
          .lt('created_at', before)
          .order('created_at', { ascending: false })
          .limit(500),
      ),
    enabled: ids.length > 0 && !!before,
  });
}

export type { TransferPlace } from '@fleetspoke/core/types';

/**
 * transfer_places(): the caller's places plus the other side by name (project users: every warehouse; warehouse
 * staff: every place). Before that function exists (a web image ahead of its migration) it is the visible sites.
 */
export function useTransferPlaces(fallback: TransferPlace[] | undefined) {
  const q = useQuery({
    queryKey: ['transfer-places'],
    queryFn: async () => {
      const { data: d, error } = await supabase().rpc('transfer_places');
      if (error) {
        if (error.code === 'PGRST202') return null; // not deployed yet
        throw new Error(error.message);
      }
      return d as TransferPlace[];
    },
  });
  return { ...q, data: q.data === null ? fallback : q.data };
}
